import * as React from 'react';

type ScaleWeightsParams = { scaleId?: string };
type ScaleWeightsOptions = { skip?: boolean };
type ScaleWeightsData = {
  scaleWeightsUpdated: {
    scaleId: string;
    grossWeight: number;
  };
};

const STABILIZATION_THRESHOLD = 6;
const UPDATE_INTERVAL_MS = 500;

const SCALE_CONFIG = {
  truck: { baseWeight: 45000, initialVariance: 100, unstableVariance: 10 },
  floor: { baseWeight: 1500, initialVariance: 20, unstableVariance: 3 },
} as const;

const STABLE_FLUCTUATION_RANGE = 0.4;

const getScaleConfig = (scaleId: string) =>
  scaleId.includes('truck') ? SCALE_CONFIG.truck : SCALE_CONFIG.floor;

const calculateFluctuation = (
  isStable: boolean,
  unstableVariance: number,
): number =>
  isStable
    ? (Math.random() - 0.5) * STABLE_FLUCTUATION_RANGE
    : (Math.random() - 0.5) * unstableVariance * 2;

const roundToOneDecimal = (value: number): number =>
  Math.round(value * 10) / 10;

const createWeightData = (
  scaleId: string,
  grossWeight: number,
): ScaleWeightsData => ({
  scaleWeightsUpdated: { scaleId, grossWeight },
});

// Mock subscription hook - simulates real-time weight updates with stabilization
// TODO: Replace with real createSubscriptionHook when backend API is ready
export const useScaleWeightsSubscription = (
  params: ScaleWeightsParams,
  options?: ScaleWeightsOptions,
): { data?: ScaleWeightsData; error?: Error } => {
  const [data, setData] = React.useState<ScaleWeightsData>();
  const readingCountRef = React.useRef(0);

  const skip = options?.skip ?? false;
  const scaleId = params.scaleId;

  React.useEffect(() => {
    if (skip || !scaleId) {
      return;
    }

    const config = getScaleConfig(scaleId);
    const targetWeight =
      config.baseWeight + Math.random() * config.initialVariance;

    // Reset reading count on new subscription
    readingCountRef.current = 0;

    // Set initial weight with significant variance (simulating load being placed)
    const initialWeight =
      targetWeight + (Math.random() - 0.5) * config.initialVariance * 2;
    setData(createWeightData(scaleId, roundToOneDecimal(initialWeight)));

    // Simulate weight updates every 500ms
    // First ~6 readings (3 seconds): large fluctuations (unstable)
    // After that: minimal fluctuations < 1 lb (stable - passes useIsWeightStable check)
    const interval = setInterval(() => {
      readingCountRef.current += 1;
      const currentCount = readingCountRef.current;

      // After 6 readings (~3 seconds), stabilize the weight
      // The stability check in useScaleReading requires 3 consecutive readings < 1 lb difference
      const isStable = currentCount > STABILIZATION_THRESHOLD;
      const fluctuation = calculateFluctuation(
        isStable,
        config.unstableVariance,
      );
      const newWeight = roundToOneDecimal(
        Math.max(0, targetWeight + fluctuation),
      );

      setData(createWeightData(scaleId, newWeight));
    }, UPDATE_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [scaleId, skip]);

  return { data, error: undefined };
};
